import { useState, useMemo, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { ArrowBack, Edit, Download, Schedule } from '@mui/icons-material';
import { Database, Loader2, TriangleAlert } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import {
  useMiseAccount,
  useMiseAccountTasks,
  useMiseAccountFeatures,
  useUpdateAccountFeatures,
  useUpdateAccountContract,
  useMiseProductGroupTypes,
  useMiseFirstLevelProductGroups,
  useMiseTableRecordCounts,
  useMiseAccountSettingsNext,
} from '@/lib/query/hooks/useMiseAccounts';
import {
  useMiseSellersCurrent,
  useMiseSellersNext,
  useMiseSellerGroupsCurrent,
  useMiseSellerGroupsNext,
  useMiseSellerSellerGroupMapCurrent,
  useMiseSellerSellerGroupMapNext,
} from '@/lib/query/hooks/useMiseSellers';
import { SellerList } from '@/components/mise/SellerList';
import { SellerGroupList } from '@/components/mise/SellerGroupList';
import { SellerSellerGroupMapList } from '@/components/mise/SellerSellerGroupMapList';
import { AccountSettingsEditDialog } from '@/components/mise/AccountSettingsEditDialog';
import { AccountSettingsNextEditDialog } from '@/components/mise/AccountSettingsNextEditDialog';
import { SellersNextEditDialog } from '@/components/mise/SellersNextEditDialog';
import { SellerGroupsNextEditDialog } from '@/components/mise/SellerGroupsNextEditDialog';
import { SellerSellerGroupMapNextEditDialog } from '@/components/mise/SellerSellerGroupMapNextEditDialog';
import { SkuCodePresenceRow } from '@/components/mise/SkuCodePresenceRow';
import { exportAccountDataAuthenticated } from '@/lib/api/mise/accounts';
import { useAuthHeader } from '@/hooks/useAuth';
import type { UpdateAccountFeaturesRequest } from '@/lib/types/mise/account';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-seller-tabpanel-${index}`}
      aria-labelledby={`account-seller-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `account-seller-tab-${index}`,
    'aria-controls': `account-seller-tabpanel-${index}`,
  };
}

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { getAuthHeader } = useAuthHeader();
  const [tabValue, setTabValue] = useState<number>(0);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [accountSettingsNextEditDialogOpen, setAccountSettingsNextEditDialogOpen] =
    useState<boolean>(false);
  const [sellersNextEditDialogOpen, setSellersNextEditDialogOpen] = useState<boolean>(false);
  const [sellerGroupsNextEditDialogOpen, setSellerGroupsNextEditDialogOpen] =
    useState<boolean>(false);
  const [sellerSellerGroupMapNextEditDialogOpen, setSellerSellerGroupMapNextEditDialogOpen] =
    useState<boolean>(false);
  const [subTabValue, setSubTabValue] = useState<number>(0);
  const [exporting, setExporting] = useState<boolean>(false);
  const [recordCountsOpen, setRecordCountsOpen] = useState<boolean>(false);

  // アカウント詳細を取得
  const {
    data: account,
    isLoading: accountLoading,
    error: accountError,
  } = useMiseAccount(id || '');

  // 予約中のバッチ集計条件（next）を取得
  const { data: accountSettingsNext } = useMiseAccountSettingsNext(id || '');

  // 集計後の店舗一覧を取得
  const {
    data: sellersCurrent,
    isLoading: sellersCurrentLoading,
    error: sellersCurrentError,
  } = useMiseSellersCurrent(id || '');

  // 集計前の店舗一覧を取得
  const {
    data: sellersNext,
    isLoading: sellersNextLoading,
    error: sellersNextError,
  } = useMiseSellersNext(id || '');

  // 集計後の店舗グループ一覧を取得
  const {
    data: sellerGroupsCurrent,
    isLoading: sellerGroupsCurrentLoading,
    error: sellerGroupsCurrentError,
  } = useMiseSellerGroupsCurrent(id || '');

  // 集計前の店舗グループ一覧を取得
  const {
    data: sellerGroupsNext,
    isLoading: sellerGroupsNextLoading,
    error: sellerGroupsNextError,
  } = useMiseSellerGroupsNext(id || '');

  // 集計後の店舗-店舗グループマッピングを取得
  const {
    data: sellerSellerGroupMapCurrent,
    isLoading: sellerSellerGroupMapCurrentLoading,
    error: sellerSellerGroupMapCurrentError,
  } = useMiseSellerSellerGroupMapCurrent(id || '');

  // 集計前の店舗-店舗グループマッピングを取得
  const {
    data: sellerSellerGroupMapNext,
    isLoading: sellerSellerGroupMapNextLoading,
    error: sellerSellerGroupMapNextError,
  } = useMiseSellerSellerGroupMapNext(id || '');

  // 商品グループタイプ一覧を取得（表示用）
  const { data: productGroupTypes } = useMiseProductGroupTypes(id || '');

  // 第1階層商品グループ一覧を取得（対象商品グループ名の表示用）
  const { data: firstLevelProductGroups } = useMiseFirstLevelProductGroups(id || '');

  // テーブルレコード件数を取得（Popover表示時のみ）
  const { data: tableRecordCounts, isLoading: tableRecordCountsLoading } = useMiseTableRecordCounts(
    id || '',
    recordCountsOpen,
  );

  // バッチタスク一覧を取得
  const { data: accountTasks, isLoading: accountTasksLoading } = useMiseAccountTasks(id || '');

  // アカウント機能設定を取得
  const { data: accountFeatures, isLoading: accountFeaturesLoading } = useMiseAccountFeatures(
    id || '',
  );
  const updateAccountFeatures = useUpdateAccountFeatures(id || '');
  const updateAccountContract = useUpdateAccountContract(id || '');

  const handleFeatureChange = async (patch: Partial<UpdateAccountFeaturesRequest>) => {
    if (!accountFeatures) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { account_id, ...current } = accountFeatures;
    try {
      await updateAccountFeatures.mutateAsync({ ...current, ...patch });
    } catch (error) {
      console.error('機能設定の更新に失敗しました:', error);
    }
  };

  const getTaskStatusColor = (status: string | null) => {
    if (!status) return 'default' as const;
    switch (status) {
      case 'Completed':
        return 'success' as const;
      case 'Failed':
        return 'error' as const;
      case 'Processing':
        return 'warning' as const;
      default:
        return 'default' as const;
    }
  };

  // 集計後: マッピングに含まれない店舗
  const unmappedSellersCurrent = useMemo(() => {
    if (!sellersCurrent || !sellerSellerGroupMapCurrent) return [];
    const mappedIds = new Set(sellerSellerGroupMapCurrent.map((m) => m.seller_id));
    return sellersCurrent.filter((s) => !mappedIds.has(s.id));
  }, [sellersCurrent, sellerSellerGroupMapCurrent]);

  // 集計前: マッピングに含まれない店舗
  const unmappedSellersNext = useMemo(() => {
    if (!sellersNext || !sellerSellerGroupMapNext) return [];
    const mappedIds = new Set(sellerSellerGroupMapNext.map((m) => m.seller_id));
    return sellersNext.filter((s) => !mappedIds.has(s.id));
  }, [sellersNext, sellerSellerGroupMapNext]);

  const handleBack = () => {
    if (account?.company_id) {
      navigate(`/mise/companies/${account.company_id}`);
    } else {
      navigate('/mise/companies');
    }
  };

  // タブ変更ハンドラー
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSubTabValue(0);
  };

  // エクスポートハンドラー
  const handleExport = async () => {
    if (!id) return;

    setExporting(true);
    try {
      const authHeaders = getAuthHeader();
      const blob = await exportAccountDataAuthenticated(id, authHeaders);
      // Blobをダウンロード
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // ファイル名はContent-Dispositionヘッダーから取得できないため、クライアント側で生成
      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      link.download = `${id}_export_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('エクスポートに失敗しました:', error);
      alert('データのエクスポートに失敗しました。');
    } finally {
      setExporting(false);
    }
  };

  // 認証ローディング中
  if (authLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>認証状態を確認中...</Typography>
      </Box>
    );
  }

  // 未認証の場合
  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>サインインページにリダイレクト中...</Typography>
      </Box>
    );
  }

  // アカウント詳細のローディング中
  if (accountLoading) {
    return (
      <AppLayout showSearch={false}>
        <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
        </Box>
      </AppLayout>
    );
  }

  // アカウント詳細のエラー
  if (accountError || !account) {
    return (
      <AppLayout showSearch={false}>
        <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
          <Alert severity="error">
            {accountError instanceof Error
              ? accountError.message
              : 'アカウントデータの読み込みに失敗しました。'}
          </Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout showSearch={false}>
      <Box sx={{ width: '90%', mx: 'auto', px: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            variant="outlined"
            sx={{ mb: 1 }}
            disabled={!account}
          >
            {account?.company_id ? '企業詳細に戻る' : '企業一覧に戻る'}
          </Button>
          <Typography variant="h6" component="h1" color="text.secondary">
            アカウント詳細
          </Typography>
        </Box>

        {/* アカウント情報（基本情報 + 設定 + 機能設定を統合） */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* ヘッダー */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant="h6">アカウント情報</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} /> : <Download />}
                  onClick={handleExport}
                  disabled={exporting}
                  size="small"
                >
                  {exporting ? 'エクスポート中...' : 'データエクスポート'}
                </Button>
                <IconButton
                  onClick={() => setEditDialogOpen(true)}
                  size="small"
                  aria-label="アカウント設定を編集"
                >
                  <Edit />
                </IconButton>
                <IconButton
                  onClick={() => setAccountSettingsNextEditDialogOpen(true)}
                  size="small"
                  aria-label="集計前の設定（バッチ集計条件）を編集"
                  title={
                    accountSettingsNext
                      ? '集計前の設定を編集（反映待ちの予約あり）'
                      : '集計前の設定を編集（翌日バッチで反映）'
                  }
                  color={accountSettingsNext ? 'warning' : 'default'}
                >
                  <Schedule />
                </IconButton>
              </Box>
            </Box>

            {/* 2カラムレイアウト */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: { xs: 'wrap', md: 'nowrap' },
              }}
            >
              {/* 左カラム: 基本情報 */}
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.1em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  基本情報
                </Typography>
                <Table
                  size="small"
                  sx={{
                    '& td': { border: 0, py: 0.75, px: 1, fontSize: '0.8125rem', lineHeight: 1.4 },
                    '& td:first-of-type': {
                      width: 180,
                      color: 'text.secondary',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    },
                    '& tr:not(:last-child) td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell>アカウントID</TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{account.account_id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>契約状況</TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={account.has_contract}
                              onChange={async (e) => {
                                try {
                                  await updateAccountContract.mutateAsync({
                                    has_contract: e.target.checked,
                                  });
                                } catch (error) {
                                  console.error('契約状態の更新に失敗しました:', error);
                                }
                              }}
                              disabled={updateAccountContract.isPending}
                              size="small"
                            />
                          }
                          label={account.has_contract ? '契約あり' : '契約なし'}
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                    </TableRow>
                    {account.account_name && (
                      <TableRow>
                        <TableCell>アカウント名</TableCell>
                        <TableCell>{account.account_name}</TableCell>
                      </TableRow>
                    )}
                    {account.company_id && (
                      <TableRow>
                        <TableCell>企業ID</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{account.company_id}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>基準日</TableCell>
                      <TableCell
                        sx={{ color: account.base_date ? 'text.primary' : 'text.disabled' }}
                      >
                        {account.base_date
                          ? new Date(account.base_date).toLocaleDateString('ja-JP')
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>last_sync_data_version</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.8125rem',
                              color:
                                account.settings?.last_sync_data_version != null
                                  ? 'text.primary'
                                  : 'text.disabled',
                            }}
                          >
                            {account.settings?.last_sync_data_version ?? '未設定'}
                          </Typography>
                          {account.settings?.last_sync_data_version != null && (
                            <Popover open={recordCountsOpen} onOpenChange={setRecordCountsOpen}>
                              <PopoverTrigger asChild>
                                <IconButton size="small" aria-label="テーブルレコード件数を表示">
                                  <Database className="w-3.5 h-3.5" />
                                </IconButton>
                              </PopoverTrigger>
                              <PopoverContent className="w-80" align="start">
                                <div className="space-y-2">
                                  <h4 className="font-medium text-sm">テーブルレコード件数</h4>
                                  {tableRecordCountsLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    </div>
                                  ) : tableRecordCounts && tableRecordCounts.length > 0 ? (
                                    <div className="space-y-1">
                                      {tableRecordCounts.map((item) => (
                                        <div
                                          key={item.table_name}
                                          className="flex justify-between text-sm"
                                        >
                                          <span className="text-muted-foreground">
                                            {item.table_name}
                                          </span>
                                          <span className="font-mono">
                                            {item.count.toLocaleString()}件
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      データがありません
                                    </p>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>last_action_data_version</TableCell>
                      <TableCell
                        sx={{
                          color:
                            account.settings?.last_action_data_version != null
                              ? 'text.primary'
                              : 'text.disabled',
                        }}
                      >
                        {account.settings?.last_action_data_version ?? '未設定'}
                      </TableCell>
                    </TableRow>
                    {/* 設定値ではなくデータの状態なので、基本情報側に置く */}
                    <SkuCodePresenceRow
                      label="JANコード"
                      present={account.settings?.has_jan_code}
                    />
                    <SkuCodePresenceRow label="QRコード" present={account.settings?.has_qr_code} />
                  </TableBody>
                </Table>
              </Box>

              {/* 右カラム: アカウント設定 */}
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    letterSpacing: '0.1em',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  アカウント設定
                </Typography>
                <Table
                  size="small"
                  sx={{
                    '& td': { border: 0, py: 0.75, px: 1, fontSize: '0.8125rem', lineHeight: 1.4 },
                    '& td:first-of-type': {
                      width: 180,
                      color: 'text.secondary',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    },
                    '& tr:not(:last-child) td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell>商品グループタイプ</TableCell>
                      <TableCell
                        sx={{
                          color: account.settings?.product_group_type_id
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {account.settings?.product_group_type_id
                          ? productGroupTypes?.find(
                              (type) => type.id === account.settings?.product_group_type_id,
                            )?.name || account.settings.product_group_type_id
                          : '未設定'}
                        {account.settings?.first_level_product_group_count != null && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            第一階層: {account.settings.first_level_product_group_count}グループ
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Hub 商品グループタイプ</TableCell>
                      <TableCell
                        sx={{
                          color: account.settings?.hub_product_group_type_ids?.length
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {account.settings?.hub_product_group_type_ids?.length
                          ? account.settings.hub_product_group_type_ids
                              .map(
                                (id) =>
                                  productGroupTypes?.find((type) => type.id === id)?.name || id,
                              )
                              .join('、')
                          : '未設定（既定タイプのみ）'}
                      </TableCell>
                    </TableRow>
                    {account.settings?.product_group_type_id && (
                      <TableRow>
                        <TableCell>アクション分析 対象商品グループ</TableCell>
                        <TableCell
                          sx={{
                            color: account.settings?.action_target_product_group_ids?.length
                              ? 'text.primary'
                              : 'text.disabled',
                          }}
                        >
                          {account.settings?.action_target_product_group_ids?.length
                            ? account.settings.action_target_product_group_ids
                                .map((id) => {
                                  const pg = firstLevelProductGroups?.find((g) => g.id === id);
                                  return pg?.name || id;
                                })
                                .join('、')
                            : '未設定（全商品のみ）'}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>LLMアクション 商品識別子</TableCell>
                      <TableCell>
                        {account.settings?.llm_action_use_id ? '品番ID' : '商品名'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>ランキングソート順</TableCell>
                      <TableCell>
                        {account.settings?.default_ranking_sort === 'sales_amount'
                          ? '販売金額'
                          : '販売数量'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>有効な集計期間</TableCell>
                      <TableCell>
                        {account.settings?.enabled_periods?.length
                          ? account.settings.enabled_periods
                              .map((p) => {
                                const labels: Record<string, string> = {
                                  CurrentDay: '前日',
                                  CurrentPreviousWeek: '前週',
                                  CurrentThisWeek: '当週',
                                  CurrentMonth: '今月',
                                  CurrentPreviousMonth: '前月',
                                };
                                return labels[p] || p;
                              })
                              .join('、')
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>サマリー集計期間</TableCell>
                      <TableCell>
                        {(() => {
                          const labels: Record<string, string> = {
                            CurrentDay: '前日',
                            CurrentPreviousWeek: '前週',
                            CurrentThisWeek: '当週',
                            CurrentMonth: '今月',
                            CurrentPreviousMonth: '前月',
                          };
                          return (
                            labels[account.settings?.summary_period ?? ''] ||
                            account.settings?.summary_period ||
                            '未設定'
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>商品詳細Athena集計期間</TableCell>
                      <TableCell>
                        {account.settings?.product_detail_aggregation_period || '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>非稼働在庫Athena集計期間</TableCell>
                      <TableCell>
                        {account.settings?.inactive_stock_aggregation_period || '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>非稼働集計日数</TableCell>
                      <TableCell
                        sx={{
                          color: account.settings?.inactive_stock_aggregation_day
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {account.settings?.inactive_stock_aggregation_day
                          ? `${account.settings.inactive_stock_aggregation_day}日`
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>店舗欠品しきい値</TableCell>
                      <TableCell
                        sx={{
                          color: account.settings?.out_of_stock_seller_threshold_stock_day
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {account.settings?.out_of_stock_seller_threshold_stock_day
                          ? `${account.settings.out_of_stock_seller_threshold_stock_day}日`
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>倉庫在庫数しきい値</TableCell>
                      <TableCell
                        sx={{
                          color:
                            account.settings
                              ?.out_of_stock_seller_threshold_warehouse_stock_quantity != null
                              ? 'text.primary'
                              : 'text.disabled',
                        }}
                      >
                        {account.settings?.out_of_stock_seller_threshold_warehouse_stock_quantity !=
                        null
                          ? `${account.settings.out_of_stock_seller_threshold_warehouse_stock_quantity}`
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>倉庫欠品しきい値</TableCell>
                      <TableCell
                        sx={{
                          color: account.settings?.out_of_stock_warehouse_threshold_stock_day
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {account.settings?.out_of_stock_warehouse_threshold_stock_day
                          ? `${account.settings.out_of_stock_warehouse_threshold_stock_day}日`
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>併売集計日数</TableCell>
                      <TableCell
                        sx={{
                          color: account.settings?.co_purchase_aggregation_day
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {account.settings?.co_purchase_aggregation_day
                          ? `${account.settings.co_purchase_aggregation_day}日`
                          : '未設定'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* 機能設定 */}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'text.secondary',
                  letterSpacing: '0.1em',
                }}
              >
                機能設定
              </Typography>
              {accountFeaturesLoading ? (
                <CircularProgress size={16} />
              ) : accountFeatures ? (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.barcode_reader}
                        onChange={(e) => handleFeatureChange({ barcode_reader: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="バーコードリーダー"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.yoy_progress}
                        onChange={(e) => handleFeatureChange({ yoy_progress: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="前年比進捗"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.qr_reader}
                        onChange={(e) => handleFeatureChange({ qr_reader: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="QRリーダー"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.vmd_share}
                        onChange={(e) => handleFeatureChange({ vmd_share: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="VMD共有"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.budget_enabled}
                        onChange={(e) => handleFeatureChange({ budget_enabled: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="予算管理"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.todo_management}
                        onChange={(e) => handleFeatureChange({ todo_management: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="TODO"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.selling_price_change}
                        onChange={(e) =>
                          handleFeatureChange({ selling_price_change: e.target.checked })
                        }
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="値付け"
                    sx={{ m: 0 }}
                  />
                </>
              ) : null}
            </Box>

            {/* StoreHub（ストアエージェントハブ）機能設定 */}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'text.secondary',
                  letterSpacing: '0.1em',
                }}
              >
                StoreHub
              </Typography>
              {accountFeaturesLoading ? (
                <CircularProgress size={16} />
              ) : accountFeatures ? (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.hub_dashboard}
                        onChange={(e) => handleFeatureChange({ hub_dashboard: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="ダッシュボード"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.hub_products}
                        onChange={(e) => handleFeatureChange({ hub_products: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="商品一覧"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.hub_agent}
                        onChange={(e) => handleFeatureChange({ hub_agent: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="エージェント"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.hub_vmd_share}
                        onChange={(e) => handleFeatureChange({ hub_vmd_share: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="VMD共有"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.hub_budget_enabled}
                        onChange={(e) =>
                          handleFeatureChange({ hub_budget_enabled: e.target.checked })
                        }
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="予算詳細"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.hub_store_usage}
                        onChange={(e) => handleFeatureChange({ hub_store_usage: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="利用状況"
                    sx={{ m: 0 }}
                  />
                </>
              ) : null}
            </Box>

            {/* LLMアクション設定 */}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'text.secondary',
                  letterSpacing: '0.1em',
                }}
              >
                LLMアクション設定
              </Typography>
              {accountFeaturesLoading ? (
                <CircularProgress size={16} />
              ) : accountFeatures ? (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.llm_action_sales}
                        onChange={(e) =>
                          handleFeatureChange({ llm_action_sales: e.target.checked })
                        }
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="販売数"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.llm_action_set}
                        onChange={(e) => handleFeatureChange({ llm_action_set: e.target.checked })}
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="セット率"
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={accountFeatures.llm_action_stock}
                        onChange={(e) =>
                          handleFeatureChange({ llm_action_stock: e.target.checked })
                        }
                        disabled={updateAccountFeatures.isPending}
                        size="small"
                      />
                    }
                    label="在庫確保"
                    sx={{ m: 0 }}
                  />
                </>
              ) : null}
            </Box>
          </CardContent>
        </Card>

        {/* バッチ実行履歴 */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              バッチ実行履歴（直近10件）
            </Typography>
            {accountTasksLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : accountTasks && accountTasks.length > 0 ? (
              <Paper variant="outlined">
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>キャッシュステータス</TableCell>
                        <TableCell>LLMステータス</TableCell>
                        <TableCell>データバージョン</TableCell>
                        <TableCell>基準日</TableCell>
                        <TableCell>作成日時</TableCell>
                        <TableCell>処理開始</TableCell>
                        <TableCell>処理終了</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accountTasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {task.id.substring(0, 8)}...
                          </TableCell>
                          <TableCell>
                            {task.batch_cache_status ? (
                              <Chip
                                label={task.batch_cache_status}
                                size="small"
                                color={getTaskStatusColor(task.batch_cache_status)}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {task.batch_llm_status ? (
                              <Chip
                                label={task.batch_llm_status}
                                size="small"
                                color={getTaskStatusColor(task.batch_llm_status)}
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {task.data_version ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {task.base_date ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {task.created_at ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {task.process_started_at ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            {task.process_finished_at ?? '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                バッチ実行履歴がありません
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* 店舗一覧タブセクション */}
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="店舗一覧タブ"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tab label={`集計後 (店舗${sellersCurrent?.length || 0}件)`} {...a11yProps(0)} />
              <Tab label={`集計前 (店舗${sellersNext?.length || 0}件)`} {...a11yProps(1)} />
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} index={0}>
            <Tabs
              value={subTabValue}
              onChange={(_e, v) => setSubTabValue(v)}
              aria-label="集計後サブタブ"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label={`店舗（${sellersCurrent?.length || 0}件）`} />
              <Tab label={`店舗グループ（${sellerGroupsCurrent?.length || 0}件）`} />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    店舗-店舗グループマッピング（{sellerSellerGroupMapCurrent?.length || 0}件）
                    {unmappedSellersCurrent.length > 0 && (
                      <TriangleAlert size={16} color="#ed6c02" />
                    )}
                  </Box>
                }
              />
            </Tabs>
            {subTabValue === 0 && (
              <SellerList
                sellers={sellersCurrent}
                isLoading={sellersCurrentLoading}
                error={sellersCurrentError}
              />
            )}
            {subTabValue === 1 && (
              <SellerGroupList
                sellerGroups={sellerGroupsCurrent}
                isLoading={sellerGroupsCurrentLoading}
                error={sellerGroupsCurrentError}
              />
            )}
            {subTabValue === 2 && (
              <SellerSellerGroupMapList
                mappings={sellerSellerGroupMapCurrent}
                sellers={sellersCurrent}
                sellerGroups={sellerGroupsCurrent}
                isLoading={sellerSellerGroupMapCurrentLoading}
                error={sellerSellerGroupMapCurrentError}
                unmappedSellers={unmappedSellersCurrent}
              />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Tabs
              value={subTabValue}
              onChange={(_e, v) => setSubTabValue(v)}
              aria-label="集計前サブタブ"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label={`店舗（${sellersNext?.length || 0}件）`} />
              <Tab label={`店舗グループ（${sellerGroupsNext?.length || 0}件）`} />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    店舗-店舗グループマッピング（{sellerSellerGroupMapNext?.length || 0}件）
                    {unmappedSellersNext.length > 0 && <TriangleAlert size={16} color="#ed6c02" />}
                  </Box>
                }
              />
            </Tabs>
            {subTabValue === 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <IconButton
                    onClick={() => setSellersNextEditDialogOpen(true)}
                    size="small"
                    aria-label="集計前の店舗を編集"
                    disabled={!account}
                  >
                    <Edit />
                  </IconButton>
                </Box>
                <SellerList
                  sellers={sellersNext}
                  isLoading={sellersNextLoading}
                  error={sellersNextError}
                />
              </>
            )}
            {subTabValue === 1 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <IconButton
                    onClick={() => setSellerGroupsNextEditDialogOpen(true)}
                    size="small"
                    aria-label="集計前の店舗グループを編集"
                    disabled={!account}
                  >
                    <Edit />
                  </IconButton>
                </Box>
                <SellerGroupList
                  sellerGroups={sellerGroupsNext}
                  isLoading={sellerGroupsNextLoading}
                  error={sellerGroupsNextError}
                />
              </>
            )}
            {subTabValue === 2 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <IconButton
                    onClick={() => setSellerSellerGroupMapNextEditDialogOpen(true)}
                    size="small"
                    aria-label="集計前のマッピングを編集"
                    disabled={!account}
                  >
                    <Edit />
                  </IconButton>
                </Box>
                <SellerSellerGroupMapList
                  mappings={sellerSellerGroupMapNext}
                  sellers={sellersNext && sellersNext.length > 0 ? sellersNext : sellersCurrent}
                  sellerGroups={
                    sellerGroupsNext && sellerGroupsNext.length > 0
                      ? sellerGroupsNext
                      : sellerGroupsCurrent
                  }
                  isLoading={sellerSellerGroupMapNextLoading}
                  error={sellerSellerGroupMapNextError}
                  unmappedSellers={unmappedSellersNext}
                />
              </>
            )}
          </TabPanel>
        </Box>

        {/* アカウント設定編集ダイアログ */}
        <AccountSettingsEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          accountId={id || ''}
          settings={account.settings}
        />

        {/* 集計前の設定（バッチ集計条件 next）編集ダイアログ */}
        <AccountSettingsNextEditDialog
          open={accountSettingsNextEditDialogOpen}
          onClose={() => setAccountSettingsNextEditDialogOpen(false)}
          accountId={id || ''}
          currentSettings={account.settings}
          pendingNext={accountSettingsNext ?? null}
        />

        {/* 集計前の店舗編集ダイアログ */}
        <SellersNextEditDialog
          open={sellersNextEditDialogOpen}
          onClose={() => setSellersNextEditDialogOpen(false)}
          accountId={id || ''}
          currentSellersNext={sellersNext}
          currentSellers={sellersCurrent}
        />

        {/* 集計前の店舗グループ編集ダイアログ */}
        <SellerGroupsNextEditDialog
          open={sellerGroupsNextEditDialogOpen}
          onClose={() => setSellerGroupsNextEditDialogOpen(false)}
          accountId={id || ''}
          currentSellerGroupsNext={sellerGroupsNext}
          currentSellerGroups={sellerGroupsCurrent}
        />

        {/* 集計前の店舗-店舗グループマッピング編集ダイアログ */}
        <SellerSellerGroupMapNextEditDialog
          open={sellerSellerGroupMapNextEditDialogOpen}
          onClose={() => setSellerSellerGroupMapNextEditDialogOpen(false)}
          accountId={id || ''}
        />
      </Box>
    </AppLayout>
  );
}
